import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { StudyUsage } from "./study-policy";
import { creditPurchaseReady } from "./study-policy";
import { STUDY_NEW_CHECKOUT_RELEASED } from "./study-billing-contract";


export const getStudyUsage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<StudyUsage> => {
    const { data, error } = await context.supabase.rpc("study_usage_state", { _user_id: context.userId });
    if (error || !data) throw new Error("Study allowance is unavailable. Please retry.");
    const usage = data as unknown as StudyUsage;
    const { readStudyRollout } = await import("./study-rollout.server");
    const rollout = await readStudyRollout(context.userId, import.meta.env.DEV);
    return { ...usage, enforcementEnabled: rollout.active };
  });

export const getQuestionPacks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: admin, error: roleError } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "super_admin" });
    if (roleError) throw new Error("Question packs are unavailable. Please retry.");
    // This is unreleased administrative configuration, not a public price list.
    if (admin !== true) return [];
    const { data, error } = await context.supabase.from("study_credit_catalog")
      .select("pack_id,name,questions,price_minor,currency,enabled,stripe_verified,apple_verified,google_verified")
      .order("questions");
    if (error) throw new Error("Question packs are unavailable. Please retry.");
    // Purchases remain unavailable until provider-specific fulfillment is verified.
    return (data ?? []).map(pack => ({ ...pack, purchasable: false as const }));
  });

export const getStudyAdminReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ days: z.union([z.literal(1), z.literal(7), z.literal(30)]) }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: report, error } = await context.supabase.rpc("study_admin_report", { _days: data.days });
    if (error) throw new Error("Conversion report is unavailable or access is denied.");
    return report;
  });

export const updateQuestionPackPrice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ packId: z.enum(["quick_boost", "study_boost", "exam_prep_boost"]), priceMinor: z.number().int().min(1).max(100000) }).parse(input))
  .handler(async ({ context, data }) => {
    const { data: admin, error } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "super_admin" });
    if (error || !admin) throw new Error("Access denied.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const result = await supabaseAdmin.from("study_credit_catalog").update({
      price_minor: data.priceMinor, enabled: false, stripe_verified: false, apple_verified: false,
      google_verified: false, updated_at: new Date().toISOString(),
    }).eq("pack_id", data.packId).select("pack_id").single();
    if (result.error) throw new Error("Could not update the proposed price.");
    return { ok: true };
  });

/** No client can activate a pack, submit a price, or grant credits. */
export const startQuestionPackPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ packId: z.enum(["quick_boost", "study_boost", "exam_prep_boost"]) }).parse(input))
  .handler(async ({ context, data }) => {
    if (!STUDY_NEW_CHECKOUT_RELEASED) return { ok: false as const, url: null, error: "Question packs are not available for purchase until payment verification and fulfillment have passed testing." };
    const { data: pack, error } = await context.supabase.from("study_credit_catalog").select("*").eq("pack_id", data.packId).single();
    if (error || !pack || !creditPurchaseReady(pack, "stripe")) return { ok: false as const, url: null, error: "Question pack is not configured." };
    const { getStripeClient } = await import("./stripe.server");
    const stripe = getStripeClient();
    if (!pack.stripe_price_id) throw new Error("Question pack is not configured.");
    const price = await stripe.prices.retrieve(pack.stripe_price_id);
    if (!price.active || price.recurring || price.unit_amount !== pack.price_minor || price.currency.toUpperCase() !== pack.currency.toUpperCase()) throw new Error("Store price does not match the verified question pack.");
    const session = await stripe.checkout.sessions.create({
      mode: "payment", client_reference_id: context.userId,
      line_items: [{ price: price.id, quantity: 1 }],
      metadata: { user_id: context.userId, study_pack_id: pack.pack_id, study_questions: String(pack.questions), study_price_minor: String(pack.price_minor) },
      success_url: "https://www.inquira.online/usage", cancel_url: "https://www.inquira.online/usage",
    });
    return { ok: true as const, url: session.url, error: null };
  });